/**
 * This device's snapshots in the local store (decision 29; part 07 log, table S). Taking, thinning and pinning run
 * one at a time, so thinning never deletes a snapshot a pin was just added to, and every write goes through the edit
 * session, so only the active tab writes (S7). A snapshot's record and its text are written and deleted together (S8).
 */
import type { ChapterText, Snapshot, SnapshotPin, SnapshotReason } from './records.ts';
import { clampTime, later, snapshotDue, thin, toSettle } from './snapshots.ts';
import type { Store, StoreFailure, WriteOp } from './store.types.ts';
import type { SessionWriteResult } from './edit-lock.ts';

/** What {@link SnapshotHistory} needs; everything with an effect is injected. */
export interface SnapshotHistoryOptions {
  readonly store: Store;
  /** The edit session's write (part 06): committed only while this tab holds the lock. */
  readonly write: (ops: readonly WriteOp[]) => Promise<SessionWriteResult>;
  /** This device's owner ID (32 lowercase hex digits; decision 29). */
  readonly owner: string;
  /** Milliseconds since the Unix epoch. */
  readonly now: () => number;
  /** The time zone offset at a time, in minutes east of UTC (`-new Date(at).getTimezoneOffset()`). */
  readonly zone: (at: number) => number;
  /** A fresh ID. */
  readonly newId: () => string;
}

/**
 * The outcome of {@link SnapshotHistory.take}: a new snapshot, the owner's last one when an interval snapshot's text is the same (S6),
 * or not written (`stale`: this tab doesn't hold the lock; `failed`: the store refused).
 */
export type TakeResult =
  | { readonly kind: 'taken'; readonly snapshot: Snapshot }
  | { readonly kind: 'unchanged'; readonly snapshot: Snapshot }
  | { readonly kind: 'stale' }
  | StoreFailure;

/**
 * The outcome of {@link SnapshotHistory.restore}: the snapshot's text for the caller to write as an ordinary edit (L4),
 * the snapshot gone, or the before-restore snapshot not written (then the caller doesn't restore).
 */
export type RestoreResult =
  | { readonly kind: 'restored'; readonly text: string; readonly before: Snapshot }
  | { readonly kind: 'missing' }
  | { readonly kind: 'stale' }
  | StoreFailure;

/**
 * The outcome of {@link SnapshotHistory.pinExport}: each chapter's pinned snapshot by chapter ID, or nothing written
 * (`stale`: this tab doesn't hold the lock; `failed`: the store refused).
 */
export type PinExportResult = { readonly kind: 'pinned'; readonly snapshots: ReadonlyMap<string, string> } | { readonly kind: 'stale' } | StoreFailure;

/** A chapter as an export took it: its record, and for a DOCX export its title (fix-next 27, table PT). */
export type ExportedChapterText = ChapterText & { readonly title?: string };

/**
 * Table PT: an export's pin until `until`: a DOCX export's with `title` when it is known (PT1, PT2), none when it isn't
 * (PT3: a reused snapshot pinned by an export of another title, or before titles were kept); a bundle's never holds one
 * (PT4).
 */
function pinOf(kind: 'docx-export' | 'bundle', until: number, title: string | undefined): SnapshotPin {
  return kind === 'docx-export' && title !== undefined ? { kind, until, title } : { kind, until };
}

/** The title a pin keeps: a DOCX export's, when it has one. */
function titleOf(pin: SnapshotPin): string | undefined {
  return pin.kind === 'docx-export' ? pin.title : undefined;
}

/** The outcome of a change to snapshots: written, not written because the lock is gone, or failed. */
export type HistoryWriteResult = SessionWriteResult | { readonly kind: 'missing' };

export class SnapshotHistory {
  private queue: Promise<unknown> = Promise.resolve();
  private readonly options: SnapshotHistoryOptions;

  constructor(options: SnapshotHistoryOptions) {
    this.options = options;
  }

  /**
   * Snapshots `chapter` as it stands (S1–S6), then thins the chapter. For R1, call it before applying the remote
   * revision, and apply it only after `taken` or `unchanged`. Never rejects.
   */
  take(chapter: ChapterText, reason: SnapshotReason): Promise<TakeResult> {
    return this.settled(() => this.takeNow(chapter, reason, new Set()));
  }

  /**
   * Restoring a snapshot (L4, S3): reads its text, snapshots `current` as `before-restore`, and returns the text for
   * the caller to write as an ordinary edit. The thinning after the snapshot never deletes the one being restored
   * (a new day's snapshot can push its day out of K2, deep-fix r4). Never rejects.
   */
  restore(current: ChapterText, snapshotId: string): Promise<RestoreResult> {
    return this.settled(async (): Promise<RestoreResult> => {
      const text = await this.text(snapshotId);
      if (text === undefined) return { kind: 'missing' };
      const before = await this.takeNow(current, 'before-restore', new Set([snapshotId]));
      if (before.kind === 'stale' || before.kind === 'failed') return before;
      return { kind: 'restored', text, before: before.snapshot };
    });
  }

  /** {@link take}'s body, run in the queue; thinning after it spares `spare`. */
  private async takeNow(chapter: ChapterText, reason: SnapshotReason, spare: ReadonlySet<string>): Promise<TakeResult> {
    const own = this.own(await this.snapshotsOf(chapter.id));
    const last = this.lastOwn(own);
    // Only an interval snapshot is skipped for text already there (S6): an event is kept 30 days (K4, S9).
    if (reason === 'interval' && last !== undefined && (await this.options.store.read('snapshotTexts', last.id))?.text === chapter.text) {
      return { kind: 'unchanged', snapshot: last };
    }
    const now = this.options.now();
    const createdAt = clampTime(now, last?.createdAt, now);
    const snapshot: Snapshot = {
      id: this.options.newId(),
      bookId: chapter.bookId,
      chapterId: chapter.id,
      owner: this.options.owner,
      seq: last === undefined ? 0 : last.seq + 1,
      createdAt,
      settled: null,
      zone: this.options.zone(createdAt),
      reason,
      pins: [],
    };
    const written = await this.options.write([
      { kind: 'put', store: 'snapshots', value: snapshot },
      { kind: 'put', store: 'snapshotTexts', value: { id: snapshot.id, text: chapter.text } },
    ]);
    if (written.kind !== 'committed') return written;
    await this.thinNow(chapter.id, spare);
    return { kind: 'taken', snapshot };
  }

  /** Whether `chapter`, which `changed` since this owner's last snapshot of it, is due an interval snapshot (S1). */
  due(chapterId: string, changed: boolean): Promise<boolean> {
    return this.serial(async () => snapshotDue(this.lastOwn(this.own(await this.snapshotsOf(chapterId))), this.options.now(), changed));
  }

  /** Thins every owner's snapshots of the chapter (K1–K4, C1, C2, P), deleting records and texts together. */
  thinChapter(chapterId: string): Promise<SessionWriteResult> {
    return this.settled(() => this.thinNow(chapterId));
  }

  /** Every readable snapshot of the chapter, all owners, oldest first. */
  list(chapterId: string): Promise<readonly Snapshot[]> {
    return this.serial(async () => [...(await this.snapshotsOf(chapterId))].sort((a, b) => a.createdAt - b.createdAt || a.seq - b.seq));
  }

  /** A snapshot's text, or undefined if it is gone. */
  async text(id: string): Promise<string | undefined> {
    return (await this.options.store.read('snapshotTexts', id))?.text;
  }

  /** Adds `pin` to the snapshot (rule P), which keeps it out of thinning. */
  pin(id: string, pin: SnapshotPin): Promise<HistoryWriteResult> {
    return this.change(id, (pins) => [...pins, pin]);
  }

  /**
   * A DOCX export's base (decision 64; part 22, table PN): each of `chapters` as the export took it is in a snapshot of
   * this owner pinned until `until`. PN1: when the owner's last snapshot of a chapter holds that text, it is the one,
   * its export pins replaced by one until the later of theirs and `until`; PN2: otherwise a new snapshot of the text,
   * pinned. PN3: every chapter read, then one write, in the queue, so nothing thins a snapshot between its read and its
   * pin, and either every chapter is pinned or none. Never rejects. Part 24d, H′1, H′3 (deep-fix r3): a bundle's hand-over
   * is pinned the same way, with pins of kind `bundle`.
   */
  pinExport(chapters: readonly ExportedChapterText[], until: number, kind: 'docx-export' | 'bundle' = 'docx-export'): Promise<PinExportResult> {
    return this.pinTexts(chapters, (chapter, pins) => {
      if (pins === null) return [pinOf(kind, until, chapter.title)];
      const exports = pins.flatMap((pin) => (pin.kind !== 'share-link' && pin.kind === kind ? [pin] : []));
      const latest = Math.max(until, ...exports.map((pin) => pin.until));
      return [...pins.filter((pin) => pin.kind !== kind), pinOf(kind, latest, exports.length === 0 || exports.every((pin) => titleOf(pin) === chapter.title) ? chapter.title : undefined)];
    });
  }

  /**
   * Part 38d, DP3 (decision 58: the snapshot behind a live link is pinned): each of `chapters` as reading link `linkId`
   * shares it is in a snapshot of this owner pinned by the link, as {@link pinExport} pins an export's (PN1–PN3): the
   * owner's last snapshot of the chapter when it holds that text, its pins kept and the link's added once, else a new one.
   */
  pinShare(chapters: readonly ChapterText[], linkId: string): Promise<PinExportResult> {
    const pin: SnapshotPin = { kind: 'share-link', linkId };
    return this.pinTexts(chapters, (_, pins) => (pins === null ? [pin] : [...pins.filter((each) => each.kind !== 'share-link' || each.linkId !== linkId), pin]));
  }

  /**
   * PN1–PN3: each of `chapters` kept in a snapshot of this owner with the pins `pinsOf` gives: its last snapshot's (given
   * them) when that holds the chapter's text, else a new snapshot's (given null); every chapter read, then one write, in
   * the queue.
   */
  private pinTexts<C extends ChapterText>(chapters: readonly C[], pinsOf: (chapter: C, pins: readonly SnapshotPin[] | null) => SnapshotPin[]): Promise<PinExportResult> {
    return this.settled(async (): Promise<PinExportResult> => {
      const ops: WriteOp[] = [];
      const snapshots = new Map<string, string>();
      const now = this.options.now();
      // Decision 14: every chapter's reads go out at once, so a long book waits on two rounds of reads, not two per chapter.
      const read = await Promise.all(
        chapters.map(async (chapter) => {
          const last = this.lastOwn(this.own(await this.snapshotsOf(chapter.id)));
          return { chapter, last, same: last !== undefined && (await this.options.store.read('snapshotTexts', last.id))?.text === chapter.text };
        }),
      );
      for (const { chapter, last, same } of read) {
        if (last !== undefined && same) {
          ops.push({ kind: 'put', store: 'snapshots', value: { ...last, pins: pinsOf(chapter, last.pins) } });
          snapshots.set(chapter.id, last.id);
          continue;
        }
        const createdAt = clampTime(now, last?.createdAt, now);
        const snapshot: Snapshot = {
          id: this.options.newId(),
          bookId: chapter.bookId,
          chapterId: chapter.id,
          owner: this.options.owner,
          seq: last === undefined ? 0 : last.seq + 1,
          createdAt,
          settled: null,
          zone: this.options.zone(createdAt),
          reason: 'interval',
          pins: pinsOf(chapter, null),
        };
        ops.push({ kind: 'put', store: 'snapshots', value: snapshot }, { kind: 'put', store: 'snapshotTexts', value: { id: snapshot.id, text: chapter.text } });
        snapshots.set(chapter.id, snapshot.id);
      }
      const written = await this.options.write(ops);
      return written.kind === 'committed' ? { kind: 'pinned', snapshots } : written;
    });
  }

  /** Removes the snapshot's pins that `which` picks; the next thinning treats it like any other once none is left. */
  unpin(id: string, which: (pin: SnapshotPin) => boolean): Promise<HistoryWriteResult> {
    return this.change(id, (pins) => pins.filter((pin) => !which(pin)));
  }

  /**
   * Runs `task` alone: after every earlier history operation has settled and before any later one starts, so a caller
   * that changes a chapter's records (part 08: moving it to the trash, deleting it forever) never interleaves with a
   * take or a thinning. A rejection comes back as `failed`. `task` must not call this history, which would wait on itself.
   */
  exclusive<T>(task: () => Promise<T>): Promise<T | StoreFailure> {
    return this.settled(task);
  }

  /** Runs `task` after every earlier one has settled; a failed task doesn't stop later ones. */
  private serial<T>(task: () => Promise<T>): Promise<T> {
    const run = this.queue.then(task);
    this.queue = run.catch(() => undefined);
    return run;
  }

  /** As {@link serial}, with a rejection turned into `failed`. */
  private settled<T>(task: () => Promise<T>): Promise<T | StoreFailure> {
    return this.serial(task).catch((error: unknown): StoreFailure => ({ kind: 'failed', error }));
  }

  private async snapshotsOf(chapterId: string): Promise<readonly Snapshot[]> {
    return (await this.options.store.readWhere('snapshots', 'chapterId', chapterId)).records;
  }

  /** This owner's snapshots among `snapshots`. */
  private own(snapshots: readonly Snapshot[]): Snapshot[] {
    return snapshots.filter((snapshot) => snapshot.owner === this.options.owner);
  }

  /** The last of `own` by `seq` (principle 2). */
  private lastOwn(own: readonly Snapshot[]): Snapshot | undefined {
    let last: Snapshot | undefined;
    for (const snapshot of own) if (last === undefined || later(snapshot, last)) last = snapshot;
    return last;
  }

  /** Thins the chapter at `now`, recording `settled` on snapshots it finds out of order (rule S), in one write. */
  private async thinNow(chapterId: string, spare: ReadonlySet<string> = new Set()): Promise<SessionWriteResult> {
    const snapshots = await this.snapshotsOf(chapterId);
    const now = this.options.now();
    const settle = toSettle(snapshots, now);
    const doomed = [...thin(snapshots, now)].filter((id) => !spare.has(id));
    return this.options.write([
      ...snapshots.filter((snapshot) => settle.has(snapshot.id)).map((snapshot): WriteOp => ({ kind: 'put', store: 'snapshots', value: { ...snapshot, settled: now } })),
      ...doomed.flatMap((id): WriteOp[] => [
        { kind: 'delete', store: 'snapshots', id },
        { kind: 'delete', store: 'snapshotTexts', id },
      ]),
    ]);
  }

  private change(id: string, pins: (current: readonly SnapshotPin[]) => SnapshotPin[]): Promise<HistoryWriteResult> {
    return this.settled(async () => {
      const snapshot = await this.options.store.read('snapshots', id);
      if (snapshot === undefined) return { kind: 'missing' };
      return this.options.write([{ kind: 'put', store: 'snapshots', value: { ...snapshot, pins: pins(snapshot.pins) } }]);
    });
  }
}
